import { useState } from 'react';
import {
  Camera,
  CaretDown,
  CaretUp,
  MicrophoneIcon,
  PaperclipIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ChatMarkdown } from './chat-markdown';
import { ChatMessageActions } from './ChatMessageActions';
import { T, useGT } from 'gt-react';
import type { MessageAttachment } from '@erp/rpc';

type MessageRole = 'user' | 'assistant';

interface ChatMessageProps {
  role: MessageRole;
  content: string;
  /**
   * What the bubble shows, when only part of `content` belongs here (the
   * rest is drawn above the turn's tools). Copy still takes all of `content`.
   */
  shown?: string;
  maxWidth?: string;
  className?: string;
  hasScreenshot?: boolean;
  screenshotDataUrl?: string;
  attachments?: MessageAttachment[];
  /**
   * Stable `ai_thread_messages.id` for an assistant turn. When present, a
   * thumbs-feedback bar is rendered directly below the bubble.
   */
  dbMessageId?: string;
  /** Langfuse traceId for the turn (passed through to feedback rows). */
  traceId?: string | null;
  /** Langfuse observation (span) id for the AI SDK's streamText span. */
  observationId?: string | null;
  /** Short label rendered above the bubble to set it apart from the main conversation. */
  badge?: string;
  /** Spoken on a voice call (or the reply to it): marked with a small tag. */
  voice?: boolean;
}

export function ChatMessage({
  role,
  content,
  shown = content,
  maxWidth = 'max-w-[85%]',
  className,
  hasScreenshot,
  screenshotDataUrl,
  attachments,
  dbMessageId,
  traceId,
  observationId,
  badge,
  voice = false,
}: ChatMessageProps) {
  const isUser = role === 'user';
  const [expanded, setExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const gt = useGT();

  if (isUser) {
    const hasContent = !!content.trim();
    return (
      <div className={cn('flex flex-col items-end gap-1', className)}>
        <div
          className={cn(
            maxWidth,
            'rounded-2xl bg-secondary px-3.5 py-2.5 text-secondary-foreground',
          )}
        >
          {hasContent && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
              {content}
            </p>
          )}
          {hasScreenshot && (
            <div
              className={cn(hasContent && 'mt-2 border-t border-border pt-2')}
            >
              <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                <Camera className="h-3.5 w-3.5 shrink-0" weight="fill" />
                <T context="label for screenshot attachment in chat message">
                  Screenshot
                </T>
                {screenshotDataUrl &&
                  (expanded ? (
                    <CaretUp className="h-3 w-3 shrink-0" weight="bold" />
                  ) : (
                    <CaretDown className="h-3 w-3 shrink-0" weight="bold" />
                  ))}
              </button>
              {expanded && screenshotDataUrl && (
                <div className="mt-1.5">
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    className="group relative inline-block cursor-pointer"
                  >
                    <img
                      src={screenshotDataUrl}
                      alt={gt('Page screenshot', {
                        context:
                          'alt text for screenshot image in chat message',
                      })}
                      className="h-16 w-24 rounded-lg border border-border object-cover"
                    />
                  </button>
                  <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
                    <DialogContent
                      className="max-w-fit sm:max-w-fit max-h-[90vh] p-2"
                      showCloseButton
                    >
                      <DialogTitle className="sr-only">
                        <T context="screen reader title for screenshot preview dialog">
                          Screenshot preview
                        </T>
                      </DialogTitle>
                      <img
                        src={screenshotDataUrl}
                        alt={gt('Page screenshot', {
                          context:
                            'alt text for screenshot image in chat message',
                        })}
                        className="max-w-[85vw] max-h-[82vh] object-contain"
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>
          )}
          {attachments && attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1 border-t border-border pt-2">
              {attachments.map((att) => (
                <div
                  key={att.documentId}
                  className="flex max-w-[180px] items-center gap-1 rounded-md bg-background/70 px-1.5 py-0.5 text-xs text-foreground"
                >
                  <PaperclipIcon className="h-3 w-3 shrink-0" />
                  <span className="truncate">{att.fileName}</span>
                </div>
              ))}
            </div>
          )}
          {!hasContent && !hasScreenshot && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
              {content}
            </p>
          )}
        </div>
        {voice && <VoiceTag />}
      </div>
    );
  }

  return (
    <div className={cn('flex w-full flex-col items-start gap-1', className)}>
      {badge && (
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
          {badge}
        </span>
      )}
      <div
        className={cn(
          maxWidth,
          badge ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {shown && <ChatMarkdown>{shown}</ChatMarkdown>}
      </div>
      <AssistantFooter
        voice={voice}
        dbMessageId={dbMessageId}
        content={content}
        traceId={traceId}
        observationId={observationId}
      />
    </div>
  );
}

/** Under an assistant reply: the voice tag and the thumbs-feedback bar. */
function AssistantFooter({
  voice,
  dbMessageId,
  content,
  traceId,
  observationId,
}: {
  voice: boolean;
  dbMessageId?: string;
  content: string;
  traceId?: string | null;
  observationId?: string | null;
}) {
  if (!voice && !dbMessageId) return null;
  return (
    <div className="flex items-center gap-2">
      {voice && <VoiceTag />}
      {dbMessageId && (
        <ChatMessageActions
          dbMessageId={dbMessageId}
          content={content}
          traceId={traceId ?? null}
          observationId={observationId ?? null}
        />
      )}
    </div>
  );
}

/** Marks a message spoken on a voice call. */
function VoiceTag() {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
      <MicrophoneIcon className="h-3 w-3" />
      <T context="small tag under a chat message that was spoken on a voice call">
        Voice
      </T>
    </span>
  );
}
